import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import { useMapLayout, MapNode } from './map/useMapLayout';
import MapCanvas from './map/MapCanvas';
import ProbeClusterOverlay from './map/ProbeClusterOverlay';
import CapabilityClusterOverlay from './map/CapabilityClusterOverlay';
import ControlsClusterOverlay from './map/ControlsClusterOverlay';
import IdentityClusterOverlay from './map/IdentityClusterOverlay';
import LineageClusterOverlay from './map/LineageClusterOverlay';
import SystemPromptOverlay from './map/SystemPromptOverlay';
import ControllerPromptOverlay from './map/ControllerPromptOverlay';
import MapTreeBrowser from './map/MapTreeBrowser';
import MapContextMenu from './map/MapContextMenu';
import { NODE_TAB_MAP, LOD_NODES } from './map/constants';

interface Transform {
  x: number;
  y: number;
  scale: number;
}

const DEFAULT_TRANSFORM: Transform = { x: 0, y: 0, scale: 0.6 };
const STORAGE_KEY_PREFIX = 'forge_map_transform_';

// ── Fly-to animation ──────────────────────────────────────────────────────────
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function flyTo(
  from: Transform,
  to: Transform,
  duration: number,
  onUpdate: (t: Transform) => void,
  onDone: () => void,
): () => void {
  const start = performance.now();
  let rafId: number;

  const tick = (now: number) => {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const ease = easeOutCubic(progress);
    onUpdate({
      x: from.x + (to.x - from.x) * ease,
      y: from.y + (to.y - from.y) * ease,
      scale: from.scale + (to.scale - from.scale) * ease,
    });
    if (progress < 1) {
      rafId = requestAnimationFrame(tick);
    } else {
      onDone();
    }
  };

  rafId = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(rafId);
}

// ── Main panel ────────────────────────────────────────────────────────────────
const MindMapPanel: React.FC = () => {
  const { darkMode } = useTheme();
  const {
    state,
    needs_recompile,
    currentPersonaId,
    pendingMapFocus,
    setPendingMapFocus,
    setActivePanel,
  } = useFabricationStore();

  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number } | null>(null);
  const handleCanvasSize = useCallback((w: number, h: number) => {
    setCanvasSize(prev => {
      if (prev && prev.w === w && prev.h === h) return prev;
      return { w, h };
    });
  }, []);
  const nodes = useMapLayout(state, canvasSize?.w ?? 1200, canvasSize?.h ?? 700);
  const hasFitRef = useRef(false);

  // Zoom-to-fit when nodes first settle AND we have real canvas dimensions
  useEffect(() => {
    if (!nodes.length || hasFitRef.current || !canvasSize) return;
    hasFitRef.current = true;
    const { w, h } = canvasSize;
    const xs = nodes.map((n) => n.x);
    const ys = nodes.map((n) => n.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const pad = 60;
    const scaleX = (w - pad * 2) / (maxX - minX || 1);
    const scaleY = (h - pad * 2) / (maxY - minY || 1);
    const scale = Math.min(scaleX, scaleY, 0.75, Math.max(scaleX, scaleY) * 0.85);
    const newTransform = {
      scale,
      x: w / 2 - ((minX + maxX) / 2) * scale,
      y: h / 2 - ((minY + maxY) / 2) * scale,
    };
    setTransform(newTransform);
  }, [nodes.length, canvasSize]);

  // Reset fit flag when persona changes so new personas get auto-fit
  useEffect(() => {
    hasFitRef.current = false;
  }, [currentPersonaId]);

  // Transform state — persisted per persona
  const storageKey = currentPersonaId
    ? `${STORAGE_KEY_PREFIX}${currentPersonaId}`
    : null;

  const [transform, setTransform] = useState<Transform>(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) return JSON.parse(saved);
      } catch {}
    }
    return DEFAULT_TRANSFORM;
  });

  // Persist transform
  useEffect(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify(transform));
  }, [transform, storageKey]);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [highlightNodeId, setHighlightNodeId] = useState<string | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const cancelFlyRef = useRef<(() => void) | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Context menu state ────────────────────────────────────────────────────
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    node: MapNode | null;
  } | null>(null);

  // ── Fly-to on pendingMapFocus ─────────────────────────────────────────────
  useEffect(() => {
    if (!pendingMapFocus || !nodes.length) return;

    const target = nodes.find((n) => n.id === pendingMapFocus);
    if (!target) return;

    setPendingMapFocus(null);

    const container = containerRef.current;
    if (!container) return;
    const W = container.offsetWidth;
    const H = container.offsetHeight;

    const targetScale = 1.0;
    const toTransform: Transform = {
      x: W / 2 - target.x * targetScale,
      y: H / 2 - target.y * targetScale,
      scale: targetScale,
    };

    if (cancelFlyRef.current) cancelFlyRef.current();
    cancelFlyRef.current = flyTo(transform, toTransform, 400, setTransform, () => {
      setSelectedNodeId(target.id);
      setHighlightNodeId(target.id);
      setTimeout(() => setHighlightNodeId(null), 2000);
    });
  }, [pendingMapFocus, nodes]);

  // Cleanup fly animation on unmount
  useEffect(() => () => { cancelFlyRef.current?.(); }, []);

  // ── Fly-to helper (used by tree browser + context menu) ───────────────────
  const flyToNode = useCallback((nodeId: string) => {
    const target = nodes.find((n) => n.id === nodeId);
    if (!target) return;
    const container = containerRef.current;
    if (!container) return;
    const W = container.offsetWidth;
    const H = container.offsetHeight;
    const pad = 80;
    // Scale so the node's bounding diameter fills ~40% of the smaller viewport dimension
    const targetScale = 4;
    const toT: Transform = {
      x: W / 2 - target.x * targetScale,
      y: H / 2 - target.y * targetScale,
      scale: targetScale,
    };
    if (cancelFlyRef.current) cancelFlyRef.current();
    cancelFlyRef.current = flyTo(transform, toT, 400, setTransform, () => {
      setSelectedNodeId(target.id);
    });
  }, [nodes, transform]);

  // ── Fit-all helper ────────────────────────────────────────────────────────
  const fitAll = useCallback(() => {
    if (!nodes.length || !canvasSize) return;
    const { w, h } = canvasSize;
    const xs = nodes.map((n) => n.x);
    const ys = nodes.map((n) => n.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const pad = 60;
    const scaleX = (w - pad * 2) / (maxX - minX || 1);
    const scaleY = (h - pad * 2) / (maxY - minY || 1);
    const scale = Math.min(scaleX, scaleY, 0.75);
    if (cancelFlyRef.current) cancelFlyRef.current();
    cancelFlyRef.current = flyTo(
      transform,
      {
        scale,
        x: w / 2 - ((minX + maxX) / 2) * scale,
        y: h / 2 - ((minY + maxY) / 2) * scale,
      },
      500,
      setTransform,
      () => {},
    );
  }, [nodes, canvasSize, transform]);

  // ── Node interactions ─────────────────────────────────────────────────────
  const handleNodeClick = useCallback((node: MapNode) => {
    setSelectedNodeId(node.id);
  }, []);

  const handleNodeHover = useCallback((node: MapNode | null) => {
    setHoveredNodeId(node?.id ?? null);
  }, []);

  const handleNodeDoubleClick = useCallback((node: MapNode) => {
    const mapping = NODE_TAB_MAP[node.type];
    if (!mapping) return;
    setActivePanel(mapping.tabIndex);
    // Scroll to target element after tab switch
    if (mapping.scrollTarget) {
      setTimeout(() => {
        const el = document.querySelector(`[data-map-id="${mapping.scrollTarget}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [setActivePanel]);

  // ── Right-click handler ───────────────────────────────────────────────────
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canvasSize) return;
    const rect = containerRef.current!.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const wx = (cx - transform.x) / transform.scale;
    const wy = (cy - transform.y) / transform.scale;
    let hit: MapNode | null = null;
    for (let i = nodes.length - 1; i >= 0; i--) {
      const n = nodes[i];
      const dx = wx - n.x;
      const dy = wy - n.y;
      if (Math.sqrt(dx * dx + dy * dy) <= n.radius + 6) { hit = n; break; }
    }
    setContextMenu({ x: e.clientX, y: e.clientY, node: hit });
  }, [nodes, transform, canvasSize]);

  // ── Legend ────────────────────────────────────────────────────────────────
  const legendItems = [
    { color: '#3b82f6', label: 'Identity Core ▱' },
    { color: '#ef4444', label: 'Tethics pillar' },
    { color: '#22c55e', label: 'Patterns pillar / Artifact' },
    { color: '#f97316', label: 'Antipatterns pillar' },
    { color: '#a855f7', label: 'Controls Cluster' },
    { color: '#f59e0b', label: 'Probe Cluster' },
    { color: '#06b6d4', label: 'Capability Cluster' },
    { color: '#6b7280', label: 'Lineage Cluster ⬡' },
  ];

  if (!state) return null;

  const hasContent = nodes.length > 0;

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 280px)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">Map</h2>
          {/* Construction banner */}
          <div
            className="relative overflow-hidden rounded px-3 py-1 flex items-center gap-1.5"
            style={{
              background: darkMode
                ? 'repeating-linear-gradient(45deg, #78350f, #78350f 8px, #92400e 8px, #92400e 16px)'
                : 'repeating-linear-gradient(45deg, #fbbf24, #fbbf24 8px, #f59e0b 8px, #f59e0b 16px)',
            }}
          >
            <span className="text-xs font-bold text-black drop-shadow-sm select-none">
              🏗 UNDER CONSTRUCTION
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Zoom controls */}
          <div className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
            darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-600'
          }`}>
            <button
              onClick={() => setTransform((t) => ({ ...t, scale: Math.max(0.1, t.scale * 0.8) }))}
              className="px-1 hover:text-blue-400"
            >−</button>
            <span className="w-10 text-center">{Math.round(transform.scale * 100)}%</span>
            <button
              onClick={() => setTransform((t) => ({ ...t, scale: Math.min(6, t.scale * 1.25) }))}
              className="px-1 hover:text-blue-400"
            >+</button>
          </div>
          {/* Reset */}
          <button
            onClick={() => setTransform(DEFAULT_TRANSFORM)}
            className={`text-xs px-2 py-1 rounded-md border ${
              darkMode
                ? 'border-gray-600 text-gray-400 hover:text-gray-200'
                : 'border-gray-300 text-gray-500 hover:text-gray-800'
            }`}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Canvas area */}
      <div
        ref={containerRef}
        className={`flex-1 rounded-lg overflow-hidden border relative ${
          darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}
        onContextMenu={handleContextMenu}
      >
        {hasContent ? (
          <>
            {/* Tree browser */}
            <MapTreeBrowser
              nodes={nodes}
              selectedNodeId={selectedNodeId}
              needsRecompile={needs_recompile}
              darkMode={darkMode}
              onFlyTo={flyToNode}
              onSelect={setSelectedNodeId}
            />

            <MapCanvas
              nodes={nodes}
              transform={transform}
              selectedNodeId={selectedNodeId}
              needsRecompile={needs_recompile}
              darkMode={darkMode}
              onNodeClick={handleNodeClick}
              onNodeDoubleClick={handleNodeDoubleClick}
              onNodeHover={handleNodeHover}
              onTransformChange={setTransform}
              onCanvasSize={handleCanvasSize}
            />
            {transform.scale >= LOD_NODES && canvasSize && (() => {
              const identityCluster   = nodes.find(n => n.type === 'identity_cluster');
              const lineageCluster    = nodes.find(n => n.type === 'lineage_cluster');
              const probeCluster      = nodes.find(n => n.type === 'probe_cluster');
              const capCluster        = nodes.find(n => n.type === 'capability_cluster');
              const controlsCluster   = nodes.find(n => n.type === 'controls_cluster');
              if (!identityCluster && !lineageCluster && !probeCluster && !capCluster && !controlsCluster) return null;
              return (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                  }}
                >
                  {identityCluster && (
                    <IdentityClusterOverlay
                      node={identityCluster}
                      transform={transform}
                      canvasWidth={canvasSize.w}
                      canvasHeight={canvasSize.h}
                      darkMode={darkMode}
                    />
                  )}
                  {lineageCluster && (
                    <LineageClusterOverlay
                      node={lineageCluster}
                      transform={transform}
                      canvasWidth={canvasSize.w}
                      canvasHeight={canvasSize.h}
                      darkMode={darkMode}
                    />
                  )}
                  {probeCluster && (
                    <ProbeClusterOverlay
                      node={probeCluster}
                      transform={transform}
                      canvasWidth={canvasSize.w}
                      canvasHeight={canvasSize.h}
                      darkMode={darkMode}
                    />
                  )}
                  {capCluster && (
                    <CapabilityClusterOverlay
                      node={capCluster}
                      transform={transform}
                      canvasWidth={canvasSize.w}
                      canvasHeight={canvasSize.h}
                      darkMode={darkMode}
                    />
                  )}
                  {controlsCluster && (
                    <ControlsClusterOverlay
                      node={controlsCluster}
                      transform={transform}
                      canvasWidth={canvasSize.w}
                      canvasHeight={canvasSize.h}
                      darkMode={darkMode}
                    />
                  )}
                  {(() => {
                    const systemPromptNode     = nodes.find(n => n.type === 'system_prompt');
                    const controllerPromptNode = nodes.find(n => n.type === 'controller_prompt');
                    return (
                      <>
                        {systemPromptNode && hoveredNodeId === systemPromptNode.id && (
                          <SystemPromptOverlay
                            node={systemPromptNode}
                            transform={transform}
                            canvasWidth={canvasSize.w}
                            canvasHeight={canvasSize.h}
                            darkMode={darkMode}
                            needsRecompile={needs_recompile}
                          />
                        )}
                        {controllerPromptNode && hoveredNodeId === controllerPromptNode.id && (
                          <ControllerPromptOverlay
                            node={controllerPromptNode}
                            transform={transform}
                            canvasWidth={canvasSize.w}
                            canvasHeight={canvasSize.h}
                            darkMode={darkMode}
                          />
                        )}
                      </>
                    );
                  })()}
                </div>
              );
            })()}
          </>
          ) : (
          <div className={`h-full flex items-center justify-center text-sm ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            <div className="text-center">
              <p className="text-2xl mb-2">🗺</p>
              <p>Build your persona to see the map.</p>
              <p className="text-xs mt-1">Start in the Source tab.</p>
            </div>
          </div>
        )}

        {/* Context menu */}
        {contextMenu && (
          <MapContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            node={contextMenu.node}
            darkMode={darkMode}
            onClose={() => setContextMenu(null)}
            onFlyTo={(id) => { flyToNode(id); setContextMenu(null); }}
            onSelect={(id) => { setSelectedNodeId(id); setContextMenu(null); }}
            onJumpToTab={(idx) => { setActivePanel(idx); setContextMenu(null); }}
            onFitAll={() => { fitAll(); setContextMenu(null); }}
            onResetZoom={() => { setTransform(DEFAULT_TRANSFORM); setContextMenu(null); }}
            onCopyTransform={() => {
              navigator.clipboard?.writeText(JSON.stringify(transform, null, 2)).catch(() => {});
              setContextMenu(null);
            }}
          />
        )}

        {/* Selected node tooltip — floats below the tree browser on the left */}
        {selectedNodeId && (() => {
          const node = nodes.find((n) => n.id === selectedNodeId);
          if (!node) return null;
          return (
            <div
              className={`absolute rounded-lg shadow-lg text-xs z-10 overflow-hidden ${
                darkMode ? 'bg-gray-800 border border-gray-600 text-gray-200' : 'bg-white border border-gray-200 text-gray-700'
              }`}
              style={{ left: 216, top: 8, width: 200 }}
            >
              {/* Panel header */}
              <div className={`px-3 py-1 border-b flex items-center ${
                darkMode ? 'bg-gray-700/60 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <span className={`text-xs font-semibold uppercase tracking-wide ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`} style={{ fontSize: 9 }}>
                  Node Navigation Hint
                </span>
              </div>
              <div className="px-3 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: node.color }} />
                  <span className="font-semibold">{node.label}</span>
                  <span className={`ml-auto text-xs opacity-60`}>{node.type.replace(/_/g, ' ')}</span>
                </div>
                <p className={`text-xs opacity-60`}>Double-click to jump to tab</p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Legend */}
      <div className={`flex-shrink-0 mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs ${
        darkMode ? 'text-gray-500' : 'text-gray-400'
      }`}>
        {legendItems.map((item) => (
          <span key={item.label} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
            {item.label}
          </span>
        ))}
        <span className="ml-auto opacity-60">Scroll to zoom · drag to pan · double-click to jump</span>
      </div>
    </div>
  );
};

export default MindMapPanel;
